import { useState, useEffect } from "react";
import { Loader2, Mail, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  if (detail && detail.msg) return detail.msg;
  return String(detail);
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.2C29.4 35.1 26.8 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.2C41.4 36 44 30.6 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

export default function AuthModal() {
  const { authOpen, setAuthOpen, setClient } = useClientAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (authOpen) { setMode("login"); setError(""); setSent(false); setForm({ name: "", email: "", password: "", phone: "" }); }
  }, [authOpen]);

  const submit = async () => {
    setBusy(true); setError("");
    try {
      if (mode === "login") {
        const { data } = await api.post("/client-auth/login", { email: form.email, password: form.password });
        setClient(data); setAuthOpen(false);
      } else if (mode === "register") {
        const { data } = await api.post("/client-auth/register", form);
        setClient(data); setAuthOpen(false);
      } else {
        await api.post("/client-auth/forgot-password", { email: form.email });
        setSent(true);
      }
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const field = (key, label, type = "text", ph = "") => (
    <div>
      <label className="eyebrow text-[#1C1917]/45 block mb-2">{label}</label>
      <input data-testid={`auth-${key}`} type={type} value={form[key]} placeholder={ph}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm transition-colors" />
    </div>
  );

  const title = mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset password";

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent data-testid="auth-modal" className="max-w-md w-[calc(100%-24px)] p-0 gap-0 bg-white border border-[#1C1917]/10 text-[#1C1917] rounded-[1.5rem] overflow-hidden">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">Sign in, create an account, or reset your password.</DialogDescription>

        <div className="px-7 pt-8 pb-2">
          <div className="eyebrow text-[#C5B382] mb-2">My Account</div>
          <h3 className="font-serif text-3xl font-medium">{title}</h3>
        </div>

        <div className="px-7 py-6 space-y-4">
          {mode === "forgot" && sent ? (
            <div data-testid="auth-forgot-sent" className="text-center py-4">
              <CheckCircle size={34} className="text-[#C5B382] mx-auto" />
              <p className="mt-3 text-sm text-[#1C1917]/70">If an account exists for <strong>{form.email}</strong>, we've emailed a reset link. It expires in 1 hour.</p>
              <button onClick={() => setMode("login")} className="mt-5 text-[#C5B382] text-sm hover:underline">Back to sign in</button>
            </div>
          ) : (
            <>
              {mode !== "forgot" && (
                <>
                  <button data-testid="auth-google" onClick={handleGoogle}
                    className="w-full flex items-center justify-center gap-3 border border-[#1C1917]/15 hover:border-[#C5B382] rounded-full py-3 text-sm font-medium transition-colors">
                    <GoogleIcon /> Continue with Google
                  </button>
                  <div className="flex items-center gap-3 text-[#1C1917]/30 text-xs">
                    <span className="flex-1 h-px bg-[#1C1917]/10" /> or <span className="flex-1 h-px bg-[#1C1917]/10" />
                  </div>
                </>
              )}

              {mode === "register" && field("name", "Full name", "text", "Jane Doe")}
              {field("email", "Email", "email", "jane@email.com")}
              {mode === "register" && field("phone", "Phone number", "tel", "04XX XXX XXX")}
              {mode !== "forgot" && field("password", "Password", "password", "••••••••")}

              {mode === "login" && (
                <button data-testid="auth-goto-forgot" onClick={() => { setMode("forgot"); setError(""); }} className="text-xs text-[#1C1917]/50 hover:text-[#C5B382] transition-colors">
                  Forgot your password?
                </button>
              )}

              {error && <p data-testid="auth-error" className="text-red-500 text-sm">{error}</p>}

              <button data-testid="auth-submit" disabled={busy} onClick={submit}
                className="w-full flex items-center justify-center gap-2 bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-50 text-[#1C1917] text-[12px] font-semibold tracking-[0.15em] uppercase py-3.5 rounded-full transition-colors">
                {busy ? <Loader2 size={16} className="animate-spin" /> : mode === "forgot" ? <Mail size={16} /> : null}
                {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
              </button>
            </>
          )}

          {!sent && (
            <div className="text-center text-sm text-[#1C1917]/55 pt-1">
              {mode === "login" && (<>New here? <button data-testid="auth-goto-register" onClick={() => { setMode("register"); setError(""); }} className="text-[#C5B382] hover:underline font-medium">Create an account</button></>)}
              {mode === "register" && (<>Already have an account? <button data-testid="auth-goto-login" onClick={() => { setMode("login"); setError(""); }} className="text-[#C5B382] hover:underline font-medium">Sign in</button></>)}
              {mode === "forgot" && (<button onClick={() => { setMode("login"); setError(""); }} className="text-[#C5B382] hover:underline font-medium">Back to sign in</button>)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
